import { useMutation } from "@tanstack/react-query";
import {
  memoRoleSchema,
  type MemoRoleValue,
  type MemoShareValue,
  type MemoView,
} from "@portal/schemas";
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTrigger,
  Label,
  NativeSelect,
  NativeSelectOption,
} from "@portal/ui";
import { UserPlus, X } from "lucide-react";
import { useState, type ReactNode } from "react";
import { updateMemoSharing } from "../../api/client";
import { useInvalidate } from "../../api/queries";
import { DialogTitleWithHint, LabelWithHint } from "../../components/Hint";
import { InlineError } from "../../components/InlineError";
import { MEMO_ROLE_LABELS } from "./memo-labels";
import { PersonName, ShareUserPicker, useUserName } from "./ShareUserPicker";

const ORG_NONE = "NONE";

/**
 * メモの共有(Google ドライブのように、個別のメールと社内の全員に、閲覧者・編集者で共有する)。
 * 変更は 1 操作ごとにすぐ保存する。所有者だけが開ける。
 * trigger を渡せばそのボタンで開く。一覧の「⋮」からは open / onOpenChange で開く。
 */
export function MemoShareDialog({
  memo,
  trigger,
  open: openProp,
  onOpenChange,
}: {
  memo: MemoView;
  trigger?: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [openState, setOpenState] = useState(false);
  const open = openProp ?? openState;
  const setOpen = onOpenChange ?? setOpenState;
  const invalidate = useInvalidate();
  // 共有の相手は、ユーザーを検索して選ぶ(ユーザー要望 2026-10-15)
  const [selected, setSelected] = useState("");
  const [role, setRole] = useState<MemoRoleValue>("VIEWER");
  const apply = useMutation({
    mutationFn: (next: { shares: MemoShareValue[]; orgAccess: MemoRoleValue | null }) =>
      updateMemoSharing(memo.id, { ...next, revision: memo.revision }),
    onSuccess: () => invalidate.memos(),
  });
  const change = (next: Partial<{ shares: MemoShareValue[]; orgAccess: MemoRoleValue | null }>) =>
    apply.mutate({ shares: memo.shares, orgAccess: memo.orgAccess, ...next });

  const nameOf = useUserName(open);
  const taken = new Set([memo.ownerEmail, ...memo.shares.map((x) => x.email)]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent>
        <DialogHeader>
          <DialogTitleWithHint hint="共有した人は、このメモを一覧から開けます。共有を変更できるのは所有者だけです。">
            メモを共有
          </DialogTitleWithHint>
        </DialogHeader>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="memo-share-user">ユーザーを追加</Label>
          <div className="flex gap-2">
            <ShareUserPicker
              id="memo-share-user"
              className="min-w-0 flex-1"
              value={selected}
              onChange={setSelected}
              exclude={taken}
              enabled={open}
            />
            <div className="shrink-0">
              <NativeSelect
                aria-label="追加する人の権限"
                value={role}
                onChange={(e) => setRole(e.target.value as MemoRoleValue)}
              >
                {memoRoleSchema.options.map((r) => (
                  <NativeSelectOption key={r} value={r}>
                    {MEMO_ROLE_LABELS[r]}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </div>
            <Button
              type="button"
              disabled={!selected || apply.isPending}
              onClick={() => {
                change({
                  shares: [
                    ...memo.shares.filter((x) => x.email !== selected),
                    { email: selected, role },
                  ],
                });
                setSelected("");
              }}
            >
              <UserPlus />
              追加
            </Button>
          </div>
        </div>

        <section aria-label="共有している人" className="flex flex-col gap-2">
          <h3 className="text-sm font-medium">アクセスできる人</h3>
          <ul className="flex flex-col divide-y rounded-md border text-sm">
            <li className="flex items-center justify-between gap-2 px-3 py-2">
              <PersonName email={memo.ownerEmail} name={nameOf(memo.ownerEmail)} />
              <span className="text-muted-foreground">所有者</span>
            </li>
            {memo.shares.map((s) => (
              <li key={s.email} className="flex items-center justify-between gap-2 px-3 py-2">
                <PersonName email={s.email} name={nameOf(s.email)} />
                <span className="flex items-center gap-1">
                  <NativeSelect
                    size="sm"
                    aria-label={`${s.email} の権限`}
                    value={s.role}
                    disabled={apply.isPending}
                    onChange={(e) =>
                      change({
                        shares: memo.shares.map((x) =>
                          x.email === s.email ? { ...x, role: e.target.value as MemoRoleValue } : x,
                        ),
                      })
                    }
                  >
                    {memoRoleSchema.options.map((r) => (
                      <NativeSelectOption key={r} value={r}>
                        {MEMO_ROLE_LABELS[r]}
                      </NativeSelectOption>
                    ))}
                  </NativeSelect>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`${s.email} の共有を外す`}
                    disabled={apply.isPending}
                    onClick={() =>
                      change({ shares: memo.shares.filter((x) => x.email !== s.email) })
                    }
                  >
                    <X />
                  </Button>
                </span>
              </li>
            ))}
          </ul>
        </section>

        <section className="flex flex-col gap-1.5">
          <LabelWithHint
            htmlFor="memo-share-org"
            hint="選ぶと、ポータルを使えるすべての人の一覧に出ます。"
          >
            社内の全員(@zenk.co.jp)
          </LabelWithHint>
          <NativeSelect
            id="memo-share-org"
            value={memo.orgAccess ?? ORG_NONE}
            disabled={apply.isPending}
            onChange={(e) =>
              change({
                orgAccess: e.target.value === ORG_NONE ? null : (e.target.value as MemoRoleValue),
              })
            }
          >
            <NativeSelectOption value={ORG_NONE}>共有しない</NativeSelectOption>
            {memoRoleSchema.options.map((r) => (
              <NativeSelectOption key={r} value={r}>
                {MEMO_ROLE_LABELS[r]}として共有
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </section>
        <InlineError error={apply.error} />
      </DialogContent>
    </Dialog>
  );
}
